import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  UploadCloud, FileText, Loader2, CheckCircle2, XCircle, AlertTriangle,
  Sparkles, Download, RefreshCw, Cpu, Plus,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { PROFESSIONS, MEDICAL_SPECIALTIES } from "../../data/site";
import { inputCls } from "../../components/forms/FormBits";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const AUTOMATED = [
  { key: "indeed", label: "Indeed" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "cv-library", label: "CV Library" },
  { key: "partner", label: "Partner Networks" },
];

const CATEGORIES = ["Ready to Submit", "Strong Prospect", "Developing", "Needs More Information"];

const csv = (v) => (Array.isArray(v) ? v.join(", ") : v || "");
const toList = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

const ScoreRing = ({ label, value }) => (
  <div className="flex flex-col items-center">
    <div className="relative flex h-16 w-16 items-center justify-center rounded-full" style={{ background: `conic-gradient(#C2751A ${(value || 0) * 3.6}deg, #EEF1F4 0deg)` }}>
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white font-serif text-sm font-bold text-navy">{value ?? "—"}</div>
    </div>
    <span className="mt-1.5 text-[11px] font-medium text-muted-foreground">{label}</span>
  </div>
);

export default function Screening() {
  const [view, setView] = useState("intake");
  const [queueFilter, setQueueFilter] = useState("Pending Review");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({ full_name: "", email: "", phone: "", profession: PROFESSIONS[0], medical_specialty: MEDICAL_SPECIALTIES[0] });
  const [active, setActive] = useState(null);      // review being reviewed
  const [draft, setDraft] = useState(null);        // editable analysis
  const [acting, setActing] = useState(false);
  const fileRef = useRef(null);

  const loadReviews = useCallback(async (status) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/screening/reviews?status=${encodeURIComponent(status)}`);
      setReviews(data);
    } catch { toast.error("Failed to load reviews"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (view === "queue") loadReviews(queueFilter); }, [view, queueFilter, loadReviews]);

  const uploadCv = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) { toast.error("CV must be PDF, DOC or DOCX."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("File must be under 10MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/screening/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("CV screened — awaiting your review");
      setView("queue"); setQueueFilter("Pending Review");
      openReview(data.review);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally { setUploading(false); }
  };

  const createManual = async () => {
    if (manual.full_name.trim().length < 2) { toast.error("Enter a full name."); return; }
    setUploading(true);
    try {
      const { data } = await api.post("/admin/screening/manual", manual);
      toast.success("Candidate created & screened — awaiting review");
      setManualOpen(false);
      setManual({ full_name: "", email: "", phone: "", profession: PROFESSIONS[0], medical_specialty: MEDICAL_SPECIALTIES[0] });
      setView("queue"); openReview(data.review);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setUploading(false); }
  };

  const openReview = (r) => {
    setActive(r);
    const a = r.analysis || {};
    setDraft({ ...a, skills: csv(a.skills), languages: csv(a.languages), strengths: csv(a.strengths), weaknesses: csv(a.weaknesses), recommendations: csv(a.recommendations) });
  };

  const approve = async () => {
    setActing(true);
    try {
      const analysis = { ...draft, skills: toList(draft.skills), languages: toList(draft.languages), strengths: toList(draft.strengths), weaknesses: toList(draft.weaknesses), recommendations: toList(draft.recommendations), quality_score: Number(draft.quality_score) || 0, readiness_score: Number(draft.readiness_score) || 0 };
      await api.post(`/admin/screening/reviews/${active.id}/approve`, { analysis });
      toast.success("Approved — applied to Candidate Intelligence Database");
      setActive(null); setDraft(null); loadReviews(queueFilter);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Approve failed."); }
    finally { setActing(false); }
  };

  const reject = async () => {
    setActing(true);
    try {
      await api.post(`/admin/screening/reviews/${active.id}/reject`, {});
      toast.success("Review rejected — candidate not updated");
      setActive(null); setDraft(null); loadReviews(queueFilter);
    } catch { toast.error("Reject failed"); }
    finally { setActing(false); }
  };

  const downloadCv = async (cv) => {
    if (!cv?.file_id) return;
    try {
      const res = await api.get(`/admin/document/${cv.file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = cv.original_filename || "cv";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div data-testid="screening-panel">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl text-navy">Candidate Screening Agent</h2>
          <p className="text-sm text-muted-foreground">Active · every CV is screened, then requires your approval before updating the database.</p>
        </div>
        <div className="flex rounded-lg border border-border bg-white p-1">
          <button onClick={() => setView("intake")} className={`rounded-md px-4 py-1.5 text-sm font-semibold ${view === "intake" ? "bg-navy text-white" : "text-charcoal/70"}`} data-testid="screening-tab-intake">Intake Gateway</button>
          <button onClick={() => setView("queue")} className={`rounded-md px-4 py-1.5 text-sm font-semibold ${view === "queue" ? "bg-navy text-white" : "text-charcoal/70"}`} data-testid="screening-tab-queue">Review Queue</button>
        </div>
      </div>

      {view === "intake" && (
        <div className="space-y-6">
          {/* Path A */}
          <div className="rounded-2xl border border-border bg-white p-6">
            <div className="flex items-center gap-2"><UploadCloud className="h-5 w-5 text-gold" /><h3 className="font-serif text-lg text-navy">Path A — Manual Intake</h3></div>
            <p className="mt-1 text-sm text-muted-foreground">Drag &amp; drop a CV to auto-screen it, or create a candidate manually.</p>
            <div
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); uploadCv(e.dataTransfer.files?.[0]); }}
              onClick={() => fileRef.current?.click()}
              className={`mt-4 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${drag ? "border-gold bg-gold/5" : "border-border bg-secondary/40 hover:border-navy/40"}`}
              data-testid="screening-dropzone"
            >
              {uploading ? <Loader2 className="h-8 w-8 animate-spin text-navy" /> : <UploadCloud className="h-8 w-8 text-navy/50" />}
              <span className="mt-2 text-sm font-semibold text-charcoal">{uploading ? "Screening…" : "Drag & drop CV here or click to upload"}</span>
              <span className="mt-1 text-xs text-muted-foreground">PDF, DOC or DOCX up to 10MB — the Screening Agent parses & scores it automatically</span>
              <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => uploadCv(e.target.files?.[0])} data-testid="screening-file-input" />
            </div>
            <button onClick={() => setManualOpen(true)} className="mt-4 inline-flex items-center gap-2 rounded-lg border border-navy/20 px-5 py-2.5 text-sm font-semibold text-navy hover:border-navy" data-testid="screening-manual-open">
              <Plus className="h-4 w-4" /> Create candidate manually
            </button>
          </div>

          {/* Path B */}
          <div className="rounded-2xl border border-border bg-white p-6">
            <div className="flex items-center gap-2"><Cpu className="h-5 w-5 text-charcoal/40" /><h3 className="font-serif text-lg text-navy">Path B — Automated Sources</h3>
              <span className="ml-2 rounded-full bg-secondary px-2.5 py-0.5 text-[11px] font-semibold text-charcoal/50">Coming Soon</span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">Webhook endpoints are live and ready. Automated ingestion activates with the Candidate Sourcing Agent in a future phase.</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {AUTOMATED.map((s) => (
                <div key={s.key} className="rounded-xl border border-border bg-background p-4" data-testid={`source-${s.key}`}>
                  <p className="font-semibold text-charcoal">{s.label}</p>
                  <span className="mt-1 inline-block rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-charcoal/50">Planned</span>
                  <p className="mt-2 break-all font-mono text-[10px] text-muted-foreground">/api/webhooks/candidate/{s.key}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {view === "queue" && (
        <div>
          <div className="mb-4 flex items-center gap-3">
            <select value={queueFilter} onChange={(e) => setQueueFilter(e.target.value)} className="rounded-lg border border-border bg-white px-4 py-2 text-sm" data-testid="queue-filter">
              {["Pending Review", "Approved", "Rejected", "all"].map((s) => <option key={s} value={s}>{s === "all" ? "All" : s}</option>)}
            </select>
            <button onClick={() => loadReviews(queueFilter)} className="inline-flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-2 text-sm text-charcoal hover:border-navy"><RefreshCw className="h-4 w-4" /> Refresh</button>
          </div>
          {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>
            : reviews.length === 0 ? <div className="rounded-2xl border border-dashed border-border bg-white py-16 text-center text-sm text-muted-foreground">No reviews in this queue.</div>
            : <div className="space-y-3" data-testid="review-list">
                {reviews.map((r) => (
                  <div key={r.id} className="rounded-2xl border border-border bg-white p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-serif text-lg text-navy">{r.candidate_name || "Unnamed candidate"}</p>
                          {!r.ai_available && <span className="rounded-full bg-gold/10 px-2 py-0.5 text-[10px] font-semibold text-gold-dark">AI offline — manual review</span>}
                          {r.duplicate && <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold text-destructive"><AlertTriangle className="h-3 w-3" /> Possible duplicate</span>}
                        </div>
                        <p className="text-sm text-muted-foreground">{r.analysis?.candidate_category} · {r.candidate_email}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <ScoreRing label="Quality" value={r.analysis?.quality_score} />
                        <ScoreRing label="Readiness" value={r.analysis?.readiness_score} />
                      </div>
                    </div>
                    {r.analysis?.ai_summary && <p className="mt-3 line-clamp-2 text-sm text-charcoal/80">{r.analysis.ai_summary}</p>}
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      {r.status === "Pending Review" ? (
                        <button onClick={() => openReview(r)} className="inline-flex items-center gap-2 rounded-lg bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy-light" data-testid={`review-open-${r.id}`}>
                          <Sparkles className="h-4 w-4" /> Review &amp; Approve
                        </button>
                      ) : (
                        <span className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold ${r.status === "Approved" ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>
                          {r.status === "Approved" ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />} {r.status}
                        </span>
                      )}
                      {r.cv?.file_id && <button onClick={() => downloadCv(r.cv)} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-semibold text-navy hover:border-navy"><Download className="h-3.5 w-3.5" /> CV</button>}
                    </div>
                  </div>
                ))}
              </div>}
        </div>
      )}

      {/* Manual create modal */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-navy">Create Candidate Manually</DialogTitle></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><label className="mb-1 block text-xs font-semibold text-charcoal/70">Full name</label><input className={inputCls} value={manual.full_name} onChange={(e) => setManual((m) => ({ ...m, full_name: e.target.value }))} data-testid="manual-name" /></div>
            <div><label className="mb-1 block text-xs font-semibold text-charcoal/70">Email</label><input className={inputCls} value={manual.email} onChange={(e) => setManual((m) => ({ ...m, email: e.target.value }))} data-testid="manual-email" /></div>
            <div><label className="mb-1 block text-xs font-semibold text-charcoal/70">Phone</label><input className={inputCls} value={manual.phone} onChange={(e) => setManual((m) => ({ ...m, phone: e.target.value }))} /></div>
            <div><label className="mb-1 block text-xs font-semibold text-charcoal/70">Profession</label><select className={inputCls} value={manual.profession} onChange={(e) => setManual((m) => ({ ...m, profession: e.target.value }))}>{PROFESSIONS.map((o) => <option key={o}>{o}</option>)}</select></div>
            <div className="sm:col-span-2"><label className="mb-1 block text-xs font-semibold text-charcoal/70">Medical specialty</label><select className={inputCls} value={manual.medical_specialty} onChange={(e) => setManual((m) => ({ ...m, medical_specialty: e.target.value }))}>{MEDICAL_SPECIALTIES.map((o) => <option key={o}>{o}</option>)}</select></div>
          </div>
          <div className="mt-4 flex justify-end gap-3">
            <button onClick={() => setManualOpen(false)} className="rounded-lg border border-border px-5 py-2.5 text-sm font-semibold text-charcoal">Cancel</button>
            <button onClick={createManual} disabled={uploading} className="inline-flex items-center gap-2 rounded-lg bg-gold px-6 py-2.5 text-sm font-semibold text-white hover:bg-gold-dark disabled:opacity-70" data-testid="manual-create">
              {uploading && <Loader2 className="h-4 w-4 animate-spin" />} Create &amp; Screen
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review & approve modal */}
      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setDraft(null); } }}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-navy">Screening Review — Human Approval</DialogTitle></DialogHeader>
          {draft && active && (
            <div className="space-y-4">
              {active.duplicate && (
                <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" data-testid="duplicate-warning">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>Possible duplicate of an existing candidate ({active.duplicate.full_name || active.duplicate.email}). Verify before approving.</span>
                </div>
              )}
              {!active.ai_available && (
                <div className="rounded-lg border border-gold/30 bg-gold/5 p-3 text-xs text-charcoal/70">
                  The AI screening service was offline for this CV, so scores are blank. Complete the fields manually, or re-run screening later once the AI key is active.
                </div>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                {[["job_title", "Job title"], ["medical_specialty", "Specialty"], ["years_experience", "Experience"], ["education", "Education"]].map(([k, l]) => (
                  <div key={k}><label className="mb-1 block text-xs font-semibold text-charcoal/70">{l}</label><input className={inputCls} value={draft[k] || ""} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} data-testid={`draft-${k}`} /></div>
                ))}
                <div><label className="mb-1 block text-xs font-semibold text-charcoal/70">Profession</label><select className={inputCls} value={draft.profession || "Other"} onChange={(e) => setDraft((d) => ({ ...d, profession: e.target.value }))}>{PROFESSIONS.map((o) => <option key={o}>{o}</option>)}</select></div>
                <div><label className="mb-1 block text-xs font-semibold text-charcoal/70">Category</label><select className={inputCls} value={draft.candidate_category || CATEGORIES[3]} onChange={(e) => setDraft((d) => ({ ...d, candidate_category: e.target.value }))} data-testid="draft-category">{CATEGORIES.map((o) => <option key={o}>{o}</option>)}</select></div>
                <div><label className="mb-1 block text-xs font-semibold text-charcoal/70">Quality score (0-100)</label><input type="number" min="0" max="100" className={inputCls} value={draft.quality_score ?? ""} onChange={(e) => setDraft((d) => ({ ...d, quality_score: e.target.value }))} data-testid="draft-quality" /></div>
                <div><label className="mb-1 block text-xs font-semibold text-charcoal/70">Readiness score (0-100)</label><input type="number" min="0" max="100" className={inputCls} value={draft.readiness_score ?? ""} onChange={(e) => setDraft((d) => ({ ...d, readiness_score: e.target.value }))} /></div>
              </div>
              <div><label className="mb-1 block text-xs font-semibold text-charcoal/70">AI summary</label><textarea rows={3} className={inputCls} value={draft.ai_summary || ""} onChange={(e) => setDraft((d) => ({ ...d, ai_summary: e.target.value }))} data-testid="draft-summary" /></div>
              {[["skills", "Skills (comma separated)"], ["languages", "Languages"], ["strengths", "Strengths"], ["weaknesses", "Weaknesses"], ["recommendations", "Recommendations"]].map(([k, l]) => (
                <div key={k}><label className="mb-1 block text-xs font-semibold text-charcoal/70">{l}</label><input className={inputCls} value={draft[k] || ""} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} /></div>
              ))}
              <div className="sticky bottom-0 flex justify-end gap-3 border-t border-border bg-white pt-4">
                <button onClick={reject} disabled={acting} className="inline-flex items-center gap-2 rounded-lg border border-destructive/30 px-5 py-2.5 text-sm font-semibold text-destructive hover:bg-destructive/5 disabled:opacity-60" data-testid="review-reject">
                  <XCircle className="h-4 w-4" /> Reject
                </button>
                <button onClick={approve} disabled={acting} className="inline-flex items-center gap-2 rounded-lg bg-success px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60" data-testid="review-approve">
                  {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Approve &amp; Apply
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
